// Notary Public — 300-keyword clustered glossary + Thai misspelling normalizer.
// Powers /notary/faq and /en/notary/faq hubs. All entries are unique A5 content.
// Do not import server code here — plain data only, safe for client bundle.

export type KeywordCluster = {
  id: string;
  titleTh: string;
  titleEn: string;
  descTh: string;
  descEn: string;
  keywords: string[];         // representative slice, not the entire 300
  linkTh: string;
  linkEn: string;
};

export const NOTARY_KEYWORD_CLUSTERS: KeywordCluster[] = [
  {
    id: "core",
    titleTh: "คำค้นหลัก & คำทั่วไป",
    titleEn: "Core & general terms",
    descTh:
      "ผู้ใช้ที่พิมพ์ 'notary public คือ' หรือ 'ทนายรับรองเอกสาร' ส่วนใหญ่กำลังหาความหมายและผู้ให้บริการที่ขึ้นทะเบียนสภาทนายความฯ เราตอบข้อสงสัยพร้อมเลขทะเบียนทนายที่ตรวจสอบได้",
    descEn:
      "Users typing 'notary public thailand' or 'notarial services attorney' usually need the definition plus a bar-registered provider. We answer with checkable Lawyers Council registration numbers.",
    keywords: [
      "notary public", "โนตารี พับลิค", "notary public คือ", "notary public thailand",
      "ทนายรับรองเอกสาร", "notarial services attorney thailand", "certified notary thailand",
    ],
    linkTh: "/notary",
    linkEn: "/en/notary",
  },
  {
    id: "acts",
    titleTh: "ประเภทการรับรอง 34 หมวด",
    titleEn: "34 specific notarial acts",
    descTh:
      "รับรองลายมือชื่อ สำเนาถูกต้อง คำสาบาน คำแปล ตัวบุคคล การมีชีวิต ลายเซ็นกรรมการ DBD และการมีอยู่ของเอกสาร — จัดกลุ่มตาม 9 หมวดหมู่มาตรฐานสภาทนายความฯ",
    descEn:
      "Signature, certified true copy, oath, translation, identity, life certificate, DBD director signature, and existence of document — grouped by the 9 Lawyers-Council categories.",
    keywords: [
      "รับรองลายมือชื่อ", "รับรองสำเนาถูกต้อง", "certified true copy thailand",
      "witness signature thailand", "notarize signature", "statutory declaration thailand",
      "certify translation notary thailand", "proof of life notary thailand",
    ],
    linkTh: "/notary#acts",
    linkEn: "/en/notary#acts",
  },
  {
    id: "personal",
    titleTh: "เอกสารส่วนบุคคล & ครอบครัว",
    titleEn: "Personal & family documents",
    descTh:
      "พาสปอร์ต บัตรประชาชน สูติบัตร ทะเบียนสมรส ใบหย่า ทรานสคริปต์ ประวัติอาชญากรรม หนังสือยินยอมให้บุตรเดินทาง — ครบทุกเอกสารที่สถานทูตต้องการก่อนออกวีซ่า",
    descEn:
      "Passport, national ID, birth/marriage/divorce certificates, transcripts, police clearance, child travel consent — every document embassies request before visa issuance.",
    keywords: [
      "notarize passport thailand", "notarize birth certificate thailand",
      "notarize marriage certificate", "notarize police clearance thailand",
      "child travel consent form thailand notary", "notarize single certificate thailand",
    ],
    linkTh: "/notary/certified-true-copy",
    linkEn: "/en/notary/certified-true-copy",
  },
  {
    id: "business",
    titleTh: "ธุรกิจ กฎหมาย & อสังหาริมทรัพย์",
    titleEn: "Business, legal & property",
    descTh:
      "หนังสือมอบอำนาจ สัญญาข้ามชาติ หนังสือรับรองบริษัท DBD Board Resolution สัญญาเช่า โฉนดที่ดิน พินัยกรรม รายงานการประชุม — เตรียมพร้อมส่ง Apostille/สถานทูตในขั้นตอนเดียว",
    descEn:
      "Power of attorney, cross-border contracts, DBD certificates, board resolutions, lease agreements, title deeds, wills, minutes — all ready for one-stop Apostille or embassy legalization.",
    keywords: [
      "notarize power of attorney", "หนังสือมอบอำนาจ notary",
      "notarize company registration thailand", "notarize board resolution",
      "notarize title deed thailand", "notarize will thailand",
    ],
    linkTh: "/notary/power-of-attorney",
    linkEn: "/en/notary/power-of-attorney",
  },
  {
    id: "location",
    titleTh: "พื้นที่ให้บริการ 77 จังหวัด",
    titleEn: "Coverage — 77 provinces",
    descTh:
      "เดินเข้ารับที่ลาดพร้าว 95 หรือ 5 จังหวัดปริมณฑล (Tier 1), ตัวแทนท้องถิ่นในเชียงใหม่/ภูเก็ต/พัทยา (Tier 2) และ Remote signing + EMS สำหรับ 66 จังหวัดที่เหลือ (Tier 3)",
    descEn:
      "Walk-in at Lat Phrao 95 or 5 metro provinces (Tier 1), local agents in Chiang Mai/Phuket/Pattaya (Tier 2), and remote signing + EMS to the remaining 66 provinces (Tier 3).",
    keywords: [
      "notary public bangkok", "notary public สุขุมวิท", "notary public เชียงใหม่",
      "notary public phuket", "notary public near bts", "notary public ใกล้ฉัน",
      "law firm notary bangkok",
    ],
    linkTh: "/notary/coverage",
    linkEn: "/en/notary/coverage",
  },
  {
    id: "price-speed",
    titleTh: "ราคา ความเร็ว & รูปแบบบริการ",
    titleEn: "Price, speed & delivery",
    descTh:
      "ค่ารับรองต่อฉบับ ฿500–2,500 · แพ็กเกจ +MFA ฿1,300–3,300 · +Apostille ฿2,000–4,000 · +Embassy ฿2,200–5,500 · เสร็จวันเดียวที่กรุงเทพฯ / 1–3 วันสำหรับต่างจังหวัด",
    descEn:
      "Notary act only THB 500–2,500 · +MFA THB 1,300–3,300 · +Apostille THB 2,000–4,000 · +Embassy THB 2,200–5,500 · same-day in Bangkok / 1–3 days upcountry.",
    keywords: [
      "notary public ราคา", "ค่าธรรมเนียม notary public", "notary public cost thailand",
      "notary public ด่วน", "same day notary bangkok", "mobile notary bangkok",
      "weekend notary bangkok",
    ],
    linkTh: "/notary#pricing",
    linkEn: "/en/notary#pricing",
  },
  {
    id: "expat",
    titleTh: "วัตถุประสงค์เฉพาะ & ชาวต่างชาติ",
    titleEn: "Specific use cases & expats",
    descTh:
      "ยื่นวีซ่า สถานทูตสหรัฐ/อังกฤษ/ออสเตรเลีย/จีน/UAE, กรมการกงสุล MFA, Apostille (Hague 2025) — ทีมทนายพูดอังกฤษ + ล่ามจีน/ญี่ปุ่น/เกาหลี/รัสเซีย/อาหรับ/เยอรมัน/ฝรั่งเศส",
    descEn:
      "US/UK/Australia/China/UAE embassy filings, Thai MFA legalization, Apostille (Hague 2025) — English-speaking attorneys with interpreters in Chinese, Japanese, Korean, Russian, Arabic, German, French.",
    keywords: [
      "notarize visa documents thailand", "notary for us citizen thailand",
      "notary for expats thailand", "english speaking lawyer notary",
      "apostille thailand", "mfa legalization thailand",
    ],
    linkTh: "/notary/for/american",
    linkEn: "/en/notary/for/american",
  },
  {
    id: "questions",
    titleTh: "คำถาม, ศัพท์เฉพาะ & คำสะกดผิด",
    titleEn: "Questions, glossary & misspellings",
    descTh:
      "ตอบคำถาม 'Notary Public ในไทยมีจริงไหม', 'Notary กับ MFA ต่างกันยังไง', 'ตราประทับ Notary หน้าตาแบบไหน' — พร้อมคำที่คนไทยมักสะกดผิด: โนตารี่พับลิค / นอทารีพับลิก / รับรองลายเซ็นต์",
    descEn:
      "Answers to 'does Thailand have Notary Public?', 'MFA vs Notary?', 'what does a Thai notarial seal look like?' — plus common misspellings we normalize automatically.",
    keywords: [
      "does thailand have notary public", "difference between mfa and notary",
      "โนตารี่พับลิค", "โนตารีพับบลิค", "นอทารีพับลิก", "รับรองลายเซ็นต์",
      "notary public vs ทนายความ",
    ],
    linkTh: "/notary/faq",
    linkEn: "/en/notary/faq",
  },
  {
    id: "purpose",
    titleTh: "วัตถุประสงค์เฉพาะ (วีซ่า/เรียน/ทำงาน)",
    titleEn: "Purpose-driven (visa / study / work)",
    descTh:
      "คนที่รู้ว่าต้องใช้ Notary เพื่ออะไร: ยื่นวีซ่า, เรียนต่อ, ทำงาน, จดทะเบียนสมรสต่างประเทศ, จดทะเบียนบริษัทต่างประเทศ, ยื่นทำนิติกรรม, ยื่นกงสุล, และสมัครงาน — เตรียมสายรับรองครบ Notary → MFA → สถานทูต หรือ Apostille",
    descEn:
      "Users who know why they need Notary: visa filing, study abroad, work permit, marriage abroad, foreign company registration, cross-border transactions, consular filing, or job application — full Notary → MFA → embassy or Apostille chain.",
    keywords: [
      "รับรองเอกสารไปต่างประเทศ", "รับรองเอกสารยื่นสถานทูต", "รับรองเอกสารยื่นวีซ่า",
      "Notary Public สำหรับวีซ่า", "Notary Public เรียนต่อ", "รับรองเอกสารทำงานต่างประเทศ",
      "รับรองเอกสารแต่งงานต่างประเทศ", "รับรองเอกสารจดทะเบียนบริษัท",
      "notary for retirement visa", "notary for golden visa portugal",
      "notary for us green card", "notary for student visa australia",
    ],
    linkTh: "/notary/use-case/visa",
    linkEn: "/en/notary/use-case/visa",
  },
  {
    id: "location-time",
    titleTh: "สถานที่ & เวลา (ใกล้ฉัน/ด่วน/นอกสถานที่)",
    titleEn: "Location & availability (near me / rush / mobile)",
    descTh:
      "Mobile Notary ทั่วกรุงเทพ + ปริมณฑล · เร่งด่วน 2 ชั่วโมง (+฿1,000) · Same-day (+฿500) · เปิดเสาร์ปกติ · อาทิตย์นัดล่วงหน้า · Bedside Notary ที่โรงพยาบาล · Airport pop-up สุวรรณภูมิ",
    descEn:
      "Mobile Notary across Bangkok + metro · 2-hour rush (+THB 1,000) · Same-day (+THB 500) · Saturdays open · Sunday by appointment · Bedside Notary at hospitals · Airport pop-up at Suvarnabhumi.",
    keywords: [
      "Notary Public ใกล้ฉัน", "ทนาย Notary ใกล้ฉัน", "Notary Public นอกสถานที่",
      "Mobile Notary Bangkok", "Notary Public ด่วน", "Notary Public เปิดวันเสาร์",
      "Notary Public เปิดวันอาทิตย์", "Notary Public นอกเวลาทำการ",
      "notary near bts", "notary near sukhumvit", "notary near suvarnabhumi",
      "bedside notary bangkok", "notary at hotel bangkok",
    ],
    linkTh: "/notary/coverage",
    linkEn: "/en/notary/coverage",
  },
  {
    id: "cost-qa",
    titleTh: "ราคา & คำถามที่พบบ่อย",
    titleEn: "Price & FAQ intent",
    descTh:
      "เปรียบเทียบค่ารับรอง · ฉบับละเท่าไหร่ · Notary ที่ไหนดี/แนะนำ · ตรวจสอบใบอนุญาตทนาย · Notary ถูกกฎหมายไหม · Notary กับ MFA/Apostille/กงสุล ต่างกันยังไง — เราตอบครบพร้อมเลขทะเบียนทนาย 6 ท่าน",
    descEn:
      "Compare notary fees · per-document cost · which notary is best · verify attorney license · is notary legal · notary vs MFA/Apostille/consul — we answer all with 6 attorneys' registration numbers.",
    keywords: [
      "Notary Public ราคา", "ค่าบริการ Notary Public", "Notary Public ฉบับละเท่าไหร่",
      "Notary Public ที่ไหนดี", "Notary Public แนะนำ", "Notary Public ถูกกฎหมาย",
      "ทนายที่ได้รับอนุญาต Notary", "Licensed Notary Public",
      "ตรวจสอบ Notary Public", "how much is notary in thailand",
      "notary vs apostille", "notary vs mfa", "compare notary services bangkok",
    ],
    linkTh: "/notary/faq",
    linkEn: "/en/notary/faq",
  },
];

// Common Thai misspellings for "Notary Public" — we render them in visible text
// (data-normalized-from) so search engines match variants without cloaking.
export const NOTARY_MISSPELLINGS = [
  { wrong: "โนตารี่พับลิค", right: "โนตารีพับลิค" },
  { wrong: "โนตารีพับบลิค", right: "โนตารีพับลิค" },
  { wrong: "โนตารี่พับลิก", right: "โนตารีพับลิค" },
  { wrong: "โนตารีพับบลิก", right: "โนตารีพับลิค" },
  { wrong: "โนตารี่พลับบลิค", right: "โนตารีพับลิค" },
  { wrong: "นอทารีพับลิก", right: "โนตารีพับลิค" },
  { wrong: "นอตารี่", right: "โนตารีพับลิค" },
  { wrong: "โนทารี่", right: "โนตารีพับลิค" },
  { wrong: "โนทารีพับลิค", right: "โนตารีพับลิค" },
  { wrong: "โนตารี่ พลับลิค", right: "โนตารีพับลิค" },
  { wrong: "โนตาลี", right: "โนตารีพับลิค" },
  { wrong: "โนต้ารี", right: "โนตารีพับลิค" },
  { wrong: "โน ตา ริ พับ ลิ ค", right: "โนตารีพับลิค" },
  { wrong: "โนตารี่ไทย", right: "โนตารีพับลิค ประเทศไทย" },
  { wrong: "โนตารี่ กทม", right: "โนตารีพับลิค กรุงเทพ" },
  { wrong: "notery public", right: "notary public" },
  { wrong: "notory public", right: "notary public" },
  { wrong: "notarypublic thailand", right: "notary public thailand" },
  { wrong: "notaraized thailand", right: "notarized thailand" },
  { wrong: "notorised thai", right: "notarised thai" },
  { wrong: "apostile thailand", right: "apostille thailand" },
  { wrong: "apostiile", right: "apostille" },
  { wrong: "รับรองลายเซ็นต์", right: "รับรองลายเซ็น" },
  { wrong: "รับรอง เอกสาร โน ตา รี", right: "รับรองเอกสาร Notary" },
  { wrong: "รับรอง เอกสาร ต่าง ประเทศ", right: "รับรองเอกสารต่างประเทศ" },
  { wrong: "เอกสาร ยื่น สถาน ทูต", right: "เอกสารยื่นสถานทูต" },
];

// ---------------------------------------------------------------------------
// Batch F — Voice-search + AI-Overview synonym clusters.
// Powers AutoLLMBlocks + AutoExtractiveBlocks so Google/ChatGPT/Perplexity
// match spoken queries and long-tail question forms. Hand-crafted, unique.
// ---------------------------------------------------------------------------

export type VoiceSynonymCluster = {
  id: string;
  intentTh: string;
  intentEn: string;
  spokenTh: string[];
  spokenEn: string[];
  answerTh: string;
  answerEn: string;
};

export const NOTARY_VOICE_SYNONYMS: VoiceSynonymCluster[] = [
  {
    id: "where-nearby",
    intentTh: "หา Notary ใกล้ฉัน",
    intentEn: "Find a notary near me",
    spokenTh: [
      "notary ใกล้ฉัน", "โนตารี่แถวนี้", "ทนายรับรองเอกสารใกล้บ้าน",
      "รับรองเอกสาร ที่ไหนใกล้ที่สุด", "ทนาย notary กทม เปิดวันเสาร์",
      "โนตารี่ ลาดพร้าว", "โนตารี่ อโศก", "โนตารี่ สีลม",
    ],
    spokenEn: [
      "notary near me bangkok", "closest notary public thailand",
      "walk-in notary bangkok saturday", "notary open now near me",
      "notary near sukhumvit", "notary near asoke bts", "notary near silom",
    ],
    answerTh:
      "NYC Notary มี 6 ทนายรับรองเอกสารประจำสำนักงานลาดพร้าว 95 · Walk-in 09:00–18:00 จ.–ส. · นัด LINE @nycli รับคิวใน 15 นาที · เดินทางถึง MRT ลาดพร้าว 6 นาที",
    answerEn:
      "NYC Notary stations 6 bar-registered attorneys at Lat Phrao 95 · walk-in Mon–Sat 09:00–18:00 · book via LINE @nycli for a 15-minute slot · 6 min from MRT Lat Phrao.",
  },
  {
    id: "same-day-rush",
    intentTh: "รับรองเอกสารด่วนวันนี้",
    intentEn: "Same-day / rush notarization",
    spokenTh: [
      "notary ด่วน วันนี้", "รับรองเอกสารด่วน 1 ชั่วโมง",
      "โนตารี่ด่วน 4 ชั่วโมง", "รับรองเอกสารรอรับได้เลย",
      "notary รอรับ", "notary express bangkok",
    ],
    spokenEn: [
      "same day notary bangkok", "1 hour notary thailand",
      "urgent notary today bangkok", "rush notarization bangkok",
      "notary while you wait bangkok",
    ],
    answerTh:
      "บริการรับรองด่วนของ NYC เสร็จภายใน 1–4 ชั่วโมง (surcharge +50%) · รอรับได้ที่สำนักงาน หรือให้พนักงานส่ง Grab ถึงมือคุณในกรุงเทพภายในวันเดียวกัน",
    answerEn:
      "NYC rush notarization completes in 1–4 hours at +50% surcharge · wait in-office or receive by Grab courier anywhere in Bangkok on the same business day.",
  },
  {
    id: "mobile-onsite",
    intentTh: "ทนาย Notary ไปหาที่บ้าน / โรงพยาบาล",
    intentEn: "Mobile / on-site notary",
    spokenTh: [
      "ทนาย notary มาที่บ้าน", "notary มาโรงพยาบาล",
      "รับรองเอกสารที่โรงแรม", "notary เยี่ยมผู้ป่วย",
      "โนตารี่นอกสถานที่ กทม", "รับรองลายเซ็นที่บ้านผู้สูงอายุ",
    ],
    spokenEn: [
      "mobile notary bangkok home visit", "notary come to my hotel bangkok",
      "hospital notary bedside thailand", "on-site notary thailand elderly",
      "notary visit condo bangkok",
    ],
    answerTh:
      "Mobile Notary ของเราไปที่บ้าน โรงแรม หรือโรงพยาบาล ทั่วกรุงเทพและปริมณฑล · ค่าเดินทาง 800–2,500 บาท · เหมาะสำหรับผู้ป่วย ผู้สูงอายุ และ POA เร่งด่วน",
    answerEn:
      "Our mobile notary team travels to homes, hotels and hospitals across Bangkok metro · trip fee THB 800–2,500 · ideal for bedside POA, elderly clients and urgent affidavits.",
  },
  {
    id: "for-embassy",
    intentTh: "รับรองเอกสารเพื่อยื่นสถานทูต",
    intentEn: "Notarize for embassy submission",
    spokenTh: [
      "รับรองเอกสารยื่นสถานทูตอเมริกา", "notary สำหรับ วีซ่า อังกฤษ",
      "รับรองยื่นสถานทูตออสเตรเลีย", "notary ยื่น visa เยอรมัน",
      "notary ยื่นสถานทูตจีน", "notary ยื่นสถานทูตญี่ปุ่น",
    ],
    spokenEn: [
      "notarize for us embassy bangkok", "notary uk visa thailand",
      "notary german embassy thailand", "notary japan embassy thailand",
      "notary china embassy thailand chain legalization",
    ],
    answerTh:
      "NYC จัดชุดรับรองครบสถานทูต — สหรัฐฯ อังกฤษ ออสเตรเลีย เยอรมัน จีน ญี่ปุ่น เกาหลี — พร้อมแปล MoJ + MFA + สถานทูต ในแพ็คเกจเดียวเสร็จ 5–10 วัน",
    answerEn:
      "NYC prepares embassy-ready packages for US, UK, AU, DE, CN, JP, KR — bundled MoJ translation + MFA legalization + embassy stamp in one 5–10 business-day flow.",
  },
  {
    id: "poa-crossborder",
    intentTh: "หนังสือมอบอำนาจข้ามประเทศ",
    intentEn: "Cross-border power of attorney",
    spokenTh: [
      "notary poa ไปต่างประเทศ", "รับรองหนังสือมอบอำนาจส่งอเมริกา",
      "poa ขายที่ดินในไทยจากต่างประเทศ", "poa เปิดบัญชีธนาคารต่างประเทศ",
      "หนังสือมอบอำนาจอสังหาริมทรัพย์ต่างประเทศ",
    ],
    spokenEn: [
      "cross border power of attorney thailand", "poa sell property thailand from abroad",
      "poa open bank account thailand", "poa apostille thailand for us court",
      "specific power of attorney thailand notary",
    ],
    answerTh:
      "หนังสือมอบอำนาจข้ามประเทศต้องผ่าน Notary + Apostille (Hague) หรือ MFA + สถานทูต (Non-Hague) · NYC ร่าง POA 7 ประเภทและทำ chain ครบใน 3–7 วันทำการ",
    answerEn:
      "A cross-border POA needs notary + Apostille (Hague route) or MFA + embassy (non-Hague) · NYC drafts 7 POA types and completes the chain in 3–7 business days.",
  },
  {
    id: "corporate-boi",
    intentTh: "รับรองเอกสารบริษัท BOI/DBD",
    intentEn: "Corporate BOI/DBD notarization",
    spokenTh: [
      "รับรองเอกสารกรรมการต่างชาติ BOI", "notary หนังสือรับรอง DBD",
      "รับรองลายเซ็นกรรมการต่างชาติ", "รับรองเอกสารเปิดบริษัทต่างประเทศ",
      "notary จดหมายจ้างงานต่างชาติ",
    ],
    spokenEn: [
      "notarize boi documents thailand", "dbd affidavit notary bangkok",
      "foreign director signature notarize thailand",
      "corporate resolution notarize thailand", "employment letter notary thailand",
    ],
    answerTh:
      "NYC รับรองเอกสาร BOI · DBD affidavit · corporate resolution · foreign director signature ครบชุด — แนบ Apostille/MFA สำหรับใช้ในบริษัทแม่ต่างประเทศ",
    answerEn:
      "NYC notarizes BOI filings, DBD affidavits, corporate resolutions and foreign-director signatures — bundled with Apostille/MFA for use by overseas parent companies.",
  },
  {
    id: "life-cert",
    intentTh: "หนังสือรับรองการมีชีวิต",
    intentEn: "Proof of life / life certificate",
    spokenTh: [
      "รับรองการมีชีวิต บำนาญ", "life certificate สำหรับ ประกันสังคม ต่างประเทศ",
      "รับรองการมีชีวิต ญี่ปุ่น", "รับรองการมีชีวิต เยอรมัน",
      "certificate of existence pension notary",
    ],
    spokenEn: [
      "proof of life notary thailand pension", "life certificate germany rentenversicherung thailand",
      "japan pension life certificate thailand notary", "us social security proof of life thailand",
    ],
    answerTh:
      "NYC ออกหนังสือรับรองการมีชีวิตให้ผู้รับบำนาญต่างประเทศ (ญี่ปุ่น เยอรมัน สหรัฐฯ อังกฤษ) · แนบ Apostille ให้พร้อมส่งกองทุนบำนาญโดยตรง",
    answerEn:
      "NYC issues proof-of-life certificates for JP, DE, US, UK pension boards · attached with Apostille and shipped directly to the overseas pension fund.",
  },
  {
    id: "esign-remote",
    intentTh: "e-Notary / Remote Notarization",
    intentEn: "e-Notary / Remote Online Notarization",
    spokenTh: [
      "e notary ไทย", "notary online ประเทศไทย",
      "รับรองลายเซ็นดิจิทัล ไทย", "โนตารี่ผ่าน zoom",
      "remote notarization thailand", "online notary bangkok",
    ],
    spokenEn: [
      "remote online notary thailand", "e-notary bangkok",
      "digital signature notary thailand", "zoom notarization thailand legal",
      "online notary for us documents thailand",
    ],
    answerTh:
      "ประเทศไทยยังไม่รับรอง RON เต็มรูปแบบตามกฎหมาย · NYC ให้บริการแบบ hybrid: video-record + wet-ink signature เพื่อความสมบูรณ์ทางกฎหมายเมื่อยื่นต่างประเทศ",
    answerEn:
      "Thailand has not enacted full RON yet · NYC offers a hybrid flow — video-recorded session plus wet-ink signature — accepted by most overseas registries and courts.",
  },
  {
    id: "translation-bundle",
    intentTh: "รับรองพร้อมแปลภาษา",
    intentEn: "Notary + certified translation bundle",
    spokenTh: [
      "notary พร้อมแปล อังกฤษ", "รับรองเอกสาร แปล จีน",
      "notary แปล ญี่ปุ่น", "รับรองเอกสาร แปล เยอรมัน MoJ",
      "แปล + notary + apostille ครบชุด",
    ],
    spokenEn: [
      "notary plus translation thailand", "certified translation and notary bundle bangkok",
      "moj translation and apostille thailand", "sworn translation notary thailand",
    ],
    answerTh:
      "แพ็คเกจ Notary + แปล MoJ + Apostille/MFA เสร็จภายใน 4–7 วันทำการ · ครอบคลุม EN ZH JA KO DE FR ES RU AR · ราคาชัดเจน ไม่มีค่าใช้จ่ายซ่อน",
    answerEn:
      "Bundle Notary + MoJ-registered translation + Apostille/MFA in 4–7 business days · covers EN ZH JA KO DE FR ES RU AR · fixed pricing, no hidden fees.",
  },
  {
    id: "verify-license",
    intentTh: "ตรวจสอบใบอนุญาต Notary",
    intentEn: "Verify a notary license",
    spokenTh: [
      "เช็ค ทนาย notary จริงไหม", "ตรวจสอบทะเบียนสภาทนายความ",
      "notary ปลอม สังเกตยังไง", "รายชื่อ Notarial Services Attorney",
      "โนตารี่ตัวจริง กรุงเทพ",
    ],
    spokenEn: [
      "verify notary thailand lawyers council", "how to check notary license thailand",
      "fake notary thailand warning signs", "list of notarial services attorney thailand",
    ],
    answerTh:
      "ตรวจสอบใบอนุญาต Notarial Services Attorney ได้ที่เว็บสภาทนายความฯ (lawyerscouncil.or.th) · NYC แสดงเลขทะเบียนทนาย 6 ท่านทุกใบรับรอง — ตรวจได้ทันที",
    answerEn:
      "Verify any Notarial Services Attorney at lawyerscouncil.or.th · NYC prints all 6 attorneys' registration numbers on every certificate for instant public verification.",
  },
];
